import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { of } from 'rxjs';
import { MessageService } from '../service-ui/message.service';
import { HttpClient } from '@angular/common/http';
import {catchError, map, tap} from 'rxjs/operators';
import { StartupService } from '../service-ui/startup.service';
import {environment} from "../../environments/environment";
import {PrintNodeOrderItem} from "../../model/PrintNodeOrderItem";
import {PrintNodeOrder} from "../../model/PrintNodeOrder";
import {NotificationsService} from "./notifications.service";
import {ManufacturingTaskBucketPeriod} from "../../model/ManufacturingTaskBucketPeriod";
import {ManufacturingTaskBucket} from "../../model/ManufacturingTaskBucket";
import {PrintNodeOrderService} from "./print-node-order.service";

@Injectable()
export class ManufacturingSetAssignerService {

  constructor(private http: HttpClient, private messageService: MessageService, private startupService: StartupService, private printNodeOrderService: PrintNodeOrderService, private notificationsService: NotificationsService) {
  }

  getURLEndPoint (): string {
    return "manufacturingsetassigner";
  }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucketPeriod */
  movePrintNodeOrderItemToManufacturingSet (printNodeOrderItemId: number, manufacturingSetId: number): Observable<PrintNodeOrderItem> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeorderitemtomanufacturingset/${printNodeOrderItemId}/${manufacturingSetId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderItemToManufacturingSet manufacturingSetId=${manufacturingSetId}  printNodeOrderItemId=${printNodeOrderItemId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(printNodeOrder => {
          const p = new PrintNodeOrder(printNodeOrder);
          const localPrintNodeOrder = this.printNodeOrderService.getLocalItem(p.ID);
          if ( localPrintNodeOrder ) {
            return this.printNodeOrderService.addUpdateLocalItem(p);
          } else {
            return this.printNodeOrderService.addUpdateLocalItem(p);
          }
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrderItemToManufacturingSet'))
      );
  }

  /**
   * PUT: move an array of `PrintNodeOrderItem`s (typically the
   * same-order siblings of one item, all currently sharing a
   * ManufacturingSet) into an existing target `ManufacturingSet`.
   * Server-side is expected to duplicate the singular endpoint's
   * per-PNOI update + notification loop over the array. Contract:
   * `docs/contracts/outbox/2026-07-21-manufacturing-set-move-siblings-existing.md`.
   */
  movePrintNodeOrderItemsToManufacturingSet (printNodeOrderItemIds: number[], manufacturingSetId: number): Observable<PrintNodeOrder> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeorderitemstomanufacturingset/${manufacturingSetId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderItemsToManufacturingSet manufacturingSetId=${manufacturingSetId}  printNodeOrderItemIds=${printNodeOrderItemIds.join(',')}`);
    return this.http.put(url, { printNodeOrderItemIds: printNodeOrderItemIds }, this.startupService.getHttpOptions())
      .pipe(
        map(printNodeOrder => {
          const p = new PrintNodeOrder(printNodeOrder);
          return this.printNodeOrderService.addUpdateLocalItem(p);
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrderItemsToManufacturingSet'))
      );
  }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucketPeriod */
  movePrintNodeOrderItemToManufacturingTaskBucketPeriod (printNodeOrderItemId: number, manufacturingTaskBucketPeriodId: number): Observable<PrintNodeOrderItem> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeorderitemtomanufacturingtaskbucketperiod/${printNodeOrderItemId}/${manufacturingTaskBucketPeriodId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderItemToManufacturingTaskBucketPeriod manufacturingTaskBucketPeriodId=${manufacturingTaskBucketPeriodId}  printNodeOrderItemId=${printNodeOrderItemId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucketPeriod => {
          const m = new ManufacturingTaskBucketPeriod(manufacturingTaskBucketPeriod);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrder'))
      );
  }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucketPeriod */
  movePrintNodeOrderItemToNextManufacturingTaskBucketPeriod (printNodeOrderItemId: number): Observable<PrintNodeOrderItem> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeorderitemtonextmanufacturingtaskbucketperiod/${printNodeOrderItemId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderItemToNextManufacturingTaskBucketPeriod printNodeOrderItemId=${printNodeOrderItemId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucketPeriod => {
          const m = new ManufacturingTaskBucketPeriod(manufacturingTaskBucketPeriod);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrderItem'))
      );
  }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucketPeriod */
  movePrintNodeOrderToNextManufacturingTaskBucketPeriod (printNodeOrderId: number): Observable<PrintNodeOrderItem> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeordertonextmanufacturingtaskbucketperiod/${printNodeOrderId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderToNextManufacturingTaskBucketPeriod  printNodeOrderItemId=${printNodeOrderId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucketPeriod => {
          const m = new ManufacturingTaskBucketPeriod(manufacturingTaskBucketPeriod);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrder'))
      );
  }

  /** PUT: create a new ManufacturingSet in the specified ManufacturingTaskBucketPeriod and move PrintNodeOrderItem to it */
  addManufacturingSetAndAssignPrintNodeOrderItemToManufacturingTaskBucket (printNodeOrderItemId: number, manufacturingTaskBucketId: number): Observable<PrintNodeOrderItem> {
     const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/addmanufacturingsetandassignprintnodeorderitemtomanufacturingtaskbucket/${printNodeOrderItemId}/${manufacturingTaskBucketId}`;
     this.messageService.add(`ManufacturingSetrAssignerService: addManufacturingSetAndAssignPrintNodeOrderItemToManufacturingTaskBucket manufacturingTaskBucketId=${manufacturingTaskBucketId}  printNodeOrderItemId=${printNodeOrderItemId}`);
     return this.http.put(url, {}, this.startupService.getHttpOptions())
        .pipe(
           map(manufacturingTaskBucket => {
              const m = new ManufacturingTaskBucket(manufacturingTaskBucket);
              return m;
           }),
           catchError(this.handleErrorX<any>('movePrintNodeOrder'))
     );
  }

  addManufacturingSetAndAssignPrintNodeOrderItemToManufacturingSet (printNodeOrderItemId: number, manufacturingTaskBucketPeriodId: number): Observable<PrintNodeOrderItem> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/addmanufacturingsetandassignprintnodeorderitemtomanufacturingset/${printNodeOrderItemId}/${manufacturingTaskBucketPeriodId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: addManufacturingSetAndAssignPrintNodeOrderItemToManufacturingSet manufacturingTaskBucketPeriodId=${manufacturingTaskBucketPeriodId}  printNodeOrderItemId=${printNodeOrderItemId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucketPeriod => {
          const m = new ManufacturingTaskBucketPeriod(manufacturingTaskBucketPeriod);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrder'))
      );
    }

  /**
   * PUT: create a new ManufacturingSet in the specified
   * ManufacturingTaskBucketPeriod and reassign every supplied
   * `PrintNodeOrderItem` (typically the same-order siblings of one
   * item that all currently share a ManufacturingSet) into it. The
   * single-item variant above is the one-item special case.
   *
   * Contract in
   * `docs/contracts/outbox/_sent/2026-07-21-manufacturing-set-move-siblings.md`;
   * shipped confirmation in
   * `docs/contracts/inbox/_processed/2026-07-21-manufacturing-set-move-siblings-shipped.md`.
   *
   * Per the shipped confirmation the endpoint returns the updated
   * `PrintNodeOrder` (matching the singular variant), NOT the
   * enclosing `ManufacturingTaskBucketPeriod` as originally
   * hypothesised. Each per-PNOI update is also emitted on the
   * notification stream so the reactive UI reduces normally; the
   * return-value handling here just funnels the reloaded
   * PrintNodeOrder through `addUpdateLocalItem` for cache
   * consistency with the other bulk variant above.
   */
  addManufacturingSetAndAssignPrintNodeOrderItemsToManufacturingSet (printNodeOrderItemIds: number[], manufacturingTaskBucketPeriodId: number): Observable<PrintNodeOrder> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/addmanufacturingsetandassignprintnodeorderitemstomanufacturingset/${manufacturingTaskBucketPeriodId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: addManufacturingSetAndAssignPrintNodeOrderItemsToManufacturingSet manufacturingTaskBucketPeriodId=${manufacturingTaskBucketPeriodId}  printNodeOrderItemIds=${printNodeOrderItemIds.join(',')}`);
    return this.http.put(url, { printNodeOrderItemIds: printNodeOrderItemIds }, this.startupService.getHttpOptions())
      .pipe(
        map(printNodeOrder => {
          const p = new PrintNodeOrder(printNodeOrder);
          return this.printNodeOrderService.addUpdateLocalItem(p);
        }),
        catchError(this.handleErrorX<any>('addManufacturingSetAndAssignPrintNodeOrderItemsToManufacturingSet'))
      );
  }

  /**
   * PUT: move a PrintNodeOrder to a different facility.
   *
   * The response is the server's authoritative PrintNodeOrder and is
   * pushed through `printNodeOrderService.addUpdateLocalItem(...)` so the
   * shared cache (and therefore the grid) reflects what actually happened.
   *
   * This matters because the server can now **refuse** the move: since
   * 2026-09-21 `changefacility` guards against moving an order whose items
   * are in a CLOSED ManufacturingSet, and a refusal returns the order
   * *unchanged* rather than erroring. Previously this method discarded the
   * response entirely, so a refused move still looked successful in the UI.
   * See `docs/contracts/inbox/_processed/2026-09-21b-manufacturingsetassigner-closed-set-guard-and-route-errors.md` §2.
   */
  changeFacility (printNodeOrder: PrintNodeOrder) {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/changefacility/${printNodeOrder.ID}/${printNodeOrder.printnode_ID}`;
    this.messageService.add(`ManufacturingSetrAssignerService: change facility printNodeOrder id=${printNodeOrder.ID} new facility ${printNodeOrder.printnode_ID}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedPrintNodeOrder => {
          const p = new PrintNodeOrder(changedPrintNodeOrder);
          return this.printNodeOrderService.addUpdateLocalItem(p);
        }),
        catchError(this.handleErrorX<any>('changeFacility'))
      );
  }

  /**
   * PUT: move a PrintNodeOrder to a different facility and set its
   * inventoryType. Same authoritative-response handling as
   * `changeFacility(...)` above, and subject to the same CLOSED-set refusal.
   */
  changeFacilityAndInventoryType (printNodeOrder: PrintNodeOrder) {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/changefacilityandinventorytype/${printNodeOrder.ID}/${printNodeOrder.printnode_ID}/${printNodeOrder.inventoryType}`;
    this.messageService.add(`ManufacturingSetrAssignerService: change facility and inventorytype printNodeOrder id=${printNodeOrder.ID} new facility ${printNodeOrder.printnode_ID} new inventoryType ${printNodeOrder.inventoryType}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(changedPrintNodeOrder => {
          const p = new PrintNodeOrder(changedPrintNodeOrder);
          return this.printNodeOrderService.addUpdateLocalItem(p);
        }),
        catchError(this.handleErrorX<any>('changeFacilityAndInventoryType'))
      );
  }

  // changeItemInventoryType (printNodeOrderItem: PrintNodeOrderItem) {
  //   const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/changeiteminventorytype/${printNodeOrderItem.ID}/${printNodeOrderItem.inventoryType}`;
  //   this.messageService.add(`ManufacturingSetrAssignerService: change facility and inventorytype printNodeOrder id=${printNodeOrderItem.ID} new inventoryType ${printNodeOrderItem.inventoryType}`);
  //   return this.http.put(url, {}, this.startupService.getHttpOptions()).
  //   pipe(tap(_ => this.log(`updated printNodeOrderItem id=${printNodeOrderItem.ID}`)), catchError(this.handleErrorX<any>('updated address in PrintNodeOrderItem')));
  // }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucket */
  movePrintNodeOrderToManufacturingTaskBucket (printNodeOrderId: number, manufacturingTaskBucketId: number): Observable<ManufacturingTaskBucket> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeordertomanufacturingtaskbucket/${printNodeOrderId}/${manufacturingTaskBucketId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderToManufacturingTaskBucket manufacturingTaskBucketId=${manufacturingTaskBucketId}  printNodeOrderId=${printNodeOrderId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucket => {
          const m = new ManufacturingTaskBucket(manufacturingTaskBucket);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrder'))
      );
  }

  /** PUT: move PrintNodeOrderItem to a specific ManufacturingTaskBucketPeriod */
  movePrintNodeOrderToManufacturingTaskBucketPeriod (printNodeOrderId: number, manufacturingTaskBucketPeriodId: number): Observable<ManufacturingTaskBucketPeriod> {
    const url = `${environment.apiBaseURL}${this.getURLEndPoint()}/moveprintnodeordertomanufacturingtaskbucketperiod/${printNodeOrderId}/${manufacturingTaskBucketPeriodId}`;
    this.messageService.add(`ManufacturingSetrAssignerService: movePrintNodeOrderToManufacturingTaskBucketPeriod manufacturingTaskBucketPeriodId=${manufacturingTaskBucketPeriodId}  printNodeOrderId=${printNodeOrderId}`);
    return this.http.put(url, {}, this.startupService.getHttpOptions())
      .pipe(
        map(manufacturingTaskBucketPeriod => {
          const m = new ManufacturingTaskBucketPeriod(manufacturingTaskBucketPeriod);
          return m;
        }),
        catchError(this.handleErrorX<any>('movePrintNodeOrder'))
      );
  }

  private handleErrorX<T> (role: string, operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {

      // TODO: send the error to remote logging infrastructure
      console.error(error, operation); // log to console instead

      // TODO: better job of transforming error for user consumption
      this.log(`${operation} failed: ${error.message}`);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
  protected log(message: string) {
    this.messageService.add(`ManufacturingSet Assigner Service:` + message);
  }

}
